import React, { useMemo } from 'react';

import { monthBands } from '../model/time-scale';
import { cssVars } from './css-vars';
import styles from './timeline-header.module.scss';

import type { DayRange } from '../model/range';
import type { TimeScale } from '../types';
import type { DayWindow } from './visible-days';

/**
 * The timeline header is always the grid's FIRST row. `aria-rowindex` is 1-based and counts every
 * row in the grid, header rows included, so body rows start at `HEADER_ROW_INDEX + 1` and the
 * owner's `aria-rowcount` has to include this row too — see `gantt-chart.tsx`, which is the one
 * place that knows both halves.
 */
export const HEADER_ROW_INDEX = 1;

type TimelineHeaderProps = {
  scale: TimeScale;
  range: DayRange;
  window: DayWindow;
  todayDay: number;
  workingWeek: readonly number[];
};

export const TimelineHeader: React.FC<TimelineHeaderProps> = ({
  scale,
  range,
  window,
  todayDay,
  workingWeek,
}) => {
  const bands = useMemo(() => monthBands(range, scale.pxPerDay), [range, scale]);

  // `range.startDay` is the coordinate origin — the same one `barLayout` uses for bars — so
  // `ticks` positions every `Tick.x` in the same system a bar's `x` is computed in, even though
  // the window this call is scoped to (`window.fromDay`/`window.toDay`) is scrolled independently
  // of the range.
  const ticks = useMemo(
    () => scale.ticks(window.fromDay, window.toDay, range.startDay, todayDay, workingWeek),
    [scale, window.fromDay, window.toDay, range.startDay, todayDay, workingWeek],
  );

  return (
    // The ticks below are `columnheader`s, which are only valid inside a `row` — and this flex
    // box already is that row. The month bands are decoration: every date they name is spelled
    // out again by the ticks under them, and as unowned text inside a row they would be read
    // out twice, so they are hidden from the accessibility tree rather than given a role.
    <div className={styles['header']} role="row" aria-rowindex={HEADER_ROW_INDEX}>
      {/* THE MONTH STRIP SPANS THE WHOLE CANVAS, ROW-HEADER COLUMN INCLUDED — it is not a child
          of `.track` any more. The strip is what makes the month name readable ON TOP OF the
          sidebar: `.bandLabel` pins to the scrollport's own left edge, and the only reason it
          could not do that before was a sticky, opaque `.corner` spacer at a higher z-index that
          ran the full header height. Nothing paints over the strip now, so the label has the
          first pixel of the scrollport to sit on.

          The bands inside it are still positioned in TIMELINE coordinates — `band.x` is measured
          from `range.startDay`, exactly like `tick.x` and a bar's `x` — so the layer holding them
          is inset by the sidebar width in the stylesheet rather than by re-deriving every band's
          offset here. */}
      <div className={styles['bandsRow']} aria-hidden="true">
        <div className={styles['bands']}>
          {bands.map((band) => (
            <div
              key={band.day}
              className={styles['band']}
              style={cssVars({ '--band-x': `${band.x}px`, '--band-w': `${band.width}px` })}
            >
              {/* N4. The `data-testid` is for `gantt-band-label.spec.ts`, which is the only
                  test that can see this element's real position: its `position: sticky` has to
                  pin to the scrollport's left edge, over the row-header column, and jsdom has no
                  layout engine to measure that with. A hashed CSS-module class is not a selector
                  an e2e should depend on. */}
              <span data-testid="band-label" className={styles['bandLabel']}>
                {band.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Same shape as the month strip above, and for the same reason: the day ruler runs the FULL
          width of the canvas, so the columns that have scrolled behind the row-header column stay
          on screen instead of disappearing into an opaque corner. There is no `.corner` spacer any
          more — `.track` is positioned off this strip rather than pushed across it — which is what
          leaves the sidebar's stretch of ruler visible. `visibleDayWindow` widens the window by
          the sidebar to keep it populated. */}
      <div className={styles['ticksRow']}>
        <div className={styles['track']}>
          <div className={styles['ticks']}>
            {ticks.map((tick) => (
              <div
                key={tick.day}
                role="columnheader"
                className={styles['tick']}
                data-today={tick.isToday}
                data-non-working={tick.isNonWorking}
                // tick.x is already relative to `range.startDay` — the origin passed into
                // `scale.ticks` above — so a tick and a bar for the same day always land on the
                // same pixel without re-deriving the multiplication here.
                style={cssVars({
                  '--tick-x': `${tick.x}px`,
                  '--tick-w': `${tick.width}px`,
                })}
              >
                {tick.subLabel ? <span>{tick.subLabel}</span> : null}
                <span>{tick.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
